import { useRef } from "react";
import { showNotification } from "../../../Common/CommonNotification";

const ATMStep = ({
  amount,
  currencyCode,
  verificationCode,
  receiptFiles = [],
  setReceiptFiles,
}) => {
  const fileInputRef = useRef(null);
  const bankDetails = {
    accountName: "Steadfast Money Transfer Pte. Ltd.",
    bankName: "DBS",
    accountNo: "0149020840",
    uen: "200609555W",
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (receiptFiles.length + files.length > 5) {
      showNotification("You can only upload up to 5 files", "error");
      return;
    }

    if (files.length > 0) {
      const newFiles = [...receiptFiles, ...files];
      setReceiptFiles(newFiles);
      showNotification(
        `${files.length} file(s) uploaded successfully`,
        "success",
      );
    }
  };

  const removeFile = (index) => {
    const newFiles = receiptFiles.filter((_, i) => i !== index);
    setReceiptFiles(newFiles);
  };

  const renderFilePreview = (file, index) => {
    const isImage = file.type.startsWith("image/");
    const previewUrl = isImage ? URL.createObjectURL(file) : null;

    return (
      <div
        key={index}
        className="position-relative me-2 mb-2"
        style={{ width: "80px", height: "80px" }}
      >
        <div
          className="border rounded-3 overflow-hidden d-flex align-items-center justify-content-center bg-light w-100 h-100"
          style={{ borderColor: "#d5dfe7" }}
        >
          {isImage ? (
            <img
              src={previewUrl}
              alt="preview"
              className="w-100 h-100 object-fit-cover"
            />
          ) : (
            <i className="fas fa-file-invoice text-primary fs-24"></i>
          )}
        </div>
        <div
          className="position-absolute bg-danger text-white rounded-circle d-flex align-items-center justify-content-center cursor-pointer"
          style={{
            top: "-8px",
            right: "-8px",
            width: "20px",
            height: "20px",
            fontSize: "10px",
            zIndex: 1,
          }}
          onClick={() => removeFile(index)}
        >
          <i className="fas fa-times"></i>
        </div>
      </div>
    );
  };

  return (
    <div className="pt-0">
      <div
        className="d-flex justify-content-between align-items-center p-3 rounded-3 mb-4"
        style={{ backgroundColor: "#f8faff" }}
      >
        <span className="fw-bold">Top up amount</span>
        <span className="text-black fw-bold fs-16">
          {amount} {currencyCode}
        </span>
      </div>

      <div className="mb-4">
        <p className="fw-bold text-black mb-3">Transfer instructions:</p>

        <div className="border rounded-2 p-4 bg-white mb-4">
          <div className="mb-3">
            <p className="text-muted mb-1 fs-14">Account name</p>
            <h5 className="fw-bold text-black mb-0">
              {bankDetails.accountName}
            </h5>
          </div>

          <div className="mb-3">
            <p className="text-muted mb-1 fs-14">Bank name</p>
            <h5 className="fw-bold text-black mb-0">{bankDetails.bankName}</h5>
          </div>

          <div className="mb-3">
            <p className="text-muted mb-1 fs-14">Account no.</p>
            <h5 className="fw-bold text-black mb-0">{bankDetails.accountNo}</h5>
          </div>

          <div className="mb-3">
            <p className="text-muted mb-1 fs-14">Unique entity number (UEN)</p>
            <h5 className="fw-bold text-black mb-0">{bankDetails.uen}</h5>
          </div>

          <div>
            <p className="text-muted mb-1 fs-14">
              Transaction verification code
            </p>
            <div className="d-flex align-items-center">
              <h5 className="fw-bold text-black mb-0 me-2">
                {verificationCode}
              </h5>
              <i
                className="far fa-copy text-muted cursor-pointer fs-18"
                onClick={() => {
                  navigator.clipboard.writeText(verificationCode);
                  showNotification(
                    "Verification code copied to clipboard",
                    "success",
                  );
                }}
              ></i>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <p className="fw-bold text-black mb-2">Upload receipt</p>
          <input
            type="file"
            ref={fileInputRef}
            className="d-none"
            accept="image/*,.pdf"
            multiple
            onChange={handleFileChange}
          />
          <div className="d-flex flex-wrap align-items-center">
            {receiptFiles.map((file, index) => renderFilePreview(file, index))}

            {receiptFiles.length < 5 && (
              <div
                className="d-flex align-items-center justify-content-center border border-dashed rounded-3 cursor-pointer mb-2"
                style={{
                  height: "80px",
                  width: "80px",
                  borderStyle: "dashed",
                  borderColor: "#d5dfe7",
                  backgroundColor: "#fff",
                }}
                onClick={() => fileInputRef.current.click()}
              >
                <i className="fas fa-upload text-muted fs-20"></i>
              </div>
            )}
          </div>
        </div>

        <div
          className="p-3 rounded-2"
          style={{ backgroundColor: "#f8faff", border: "1px solid #eef2ff" }}
        >
          <div className="d-flex gap-3 mb-3 pt-1">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
              style={{
                backgroundColor: "#FEF9C3",
                width: "32px",
                height: "32px",
              }}
            >
              <i className="fas fa-exclamation text-warning"></i>
            </div>
            <p className="mb-0 fs-13 text-muted" style={{ lineHeight: "1.5" }}>
              Please enter the{" "}
              <span className="fw-bold text-black">
                EXACT Transaction Verification Code
              </span>{" "}
              in your transfer description. If the code is missing/incorrect,
              you must contact Support.
            </p>
          </div>
          <div className="d-flex gap-3 pb-1">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
              style={{
                backgroundColor: "#FFEDD5",
                width: "32px",
                height: "32px",
              }}
            >
              <i
                className="far fa-clock text-orange"
                style={{ color: "#F97316" }}
              ></i>
            </div>
            <p className="mb-0 fs-13 text-muted" style={{ lineHeight: "1.5" }}>
              Please allow up to 2 hours during business hours for the top-up
              amount to be reflected in your main balance. Until then, the
              amount will be shown as "pending".
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ATMStep;
